import React, { useState, useEffect, createContext, useContext } from 'react';
import PropTypes from 'prop-types';

const defaultValue = {};

const defaultQueries = {
  xs: '(min-width: 0px)',
  sm: '(min-width: 600px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1279px)',
  xxl: '(min-width: 1449px)',
  xxxl: '(min-width: 1679px)',
  horizontal: '(orientation: landscape)'
};

const BreakpointContext = createContext(defaultValue);

const BreakpointProvider = ({ children, queries = defaultQueries }) => {
  const [queryMatch, setQueryMatch] = useState({});

  useEffect(() => {
    const mediaQueryLists = {};
    const keys = Object.keys(queries);
    let isAttached = false;

    const handleQueryListener = () => {
      const updatedMatches = keys.reduce((acc, media) => {
        acc[media] = !!(
          mediaQueryLists[media] && mediaQueryLists[media].matches
        );
        return acc;
      }, {});
      setQueryMatch(updatedMatches);
    };

    if (window && window.matchMedia) {
      const matches = {};
      keys.forEach((media) => {
        if (typeof queries[media] === 'string') {
          mediaQueryLists[media] = window.matchMedia(queries[media]);
          matches[media] = mediaQueryLists[media].matches;
        } else {
          matches[media] = false;
        }
      });
      setQueryMatch(matches);
      isAttached = true;
      keys.forEach((media) => {
        if (typeof queries[media] === 'string') {
          mediaQueryLists[media].addListener(handleQueryListener);
        }
      });
    }

    return () => {
      if (isAttached) {
        keys.forEach((media) => {
          if (typeof queries[media] === 'string') {
            mediaQueryLists[media].removeListener(handleQueryListener);
          }
        });
      }
    };
  }, [queries]);

  return (
    <BreakpointContext.Provider value={queryMatch}>
      {children}
    </BreakpointContext.Provider>
  );
};

BreakpointProvider.propTypes = {
  children: PropTypes.node,
  queries: PropTypes.object
};

/**
 * @typedef BreakpointHookValues
 * @property {boolean} xs - true if screen size is > XS breakpoint
 * @property {boolean} sm - true if screen size is > SM breakpoint
 * @property {boolean} md - true if screen size is > MD breakpoint
 * @property {boolean} lg - true if screen size is > LG breakpoint
 * @property {boolean} xl - true if screen size is > XL breakpoint
 * @property {boolean} xxl - true if screen size is > XXL breakpoint
 * @property {boolean} xxxl - true if screen size is > XXXL breakpoint
 * @property {Function} getCurrentBreakpoint - returns the active breakpoint string (i.e. "xs")
 */
/**
 *
 * @returns {BreakpointHookValues}
 */
function useBreakpoints () {
  const context = useContext(BreakpointContext);
  if (context === defaultValue) {
    throw new Error('useBreakpoints must be used within BreakpointProvider');
  }

  //TODO-JJ: document this breakpoint logic and how it can be used for question types
  const getCurrentBreakpoint = () => {
    const sizes = ['xxxl', 'xxl', 'xl', 'lg', 'md', 'sm', 'xs'];
    let matchedSize = false;
    let index = -1;
    do {
      index++;
      matchedSize = context[sizes[index]] === true;
    } while (!matchedSize &&  index < sizes.length);
    return sizes[index];
  };

  return {
    ...context,
    getCurrentBreakpoint
  };
}

export { BreakpointProvider, BreakpointContext, useBreakpoints };
